import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Plus, Timer, Trash2 } from 'lucide-react';
import {
  STAGE_SITUATIONS,
  initialStageSituation,
  situationLabel,
  type DeskDetail,
  type DeskStage,
  type StageBody,
  type StageSituation,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge, Dialog } from '@/components/ui/misc';
import { errorMessage } from '@/features/desks/desks-queries';
import { useTenantProfile } from '@/shared/tenant-profile';

/** Botão de ação quieto usado nas linhas das listas de configuração. */
export function DeskRowAction({
  label,
  onClick,
  disabled,
  destructive,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={
        destructive
          ? 'flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-40'
          : 'flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40'
      }
    >
      {children}
    </button>
  );
}

export function DeskStagesSection({ desk }: { desk: DeskDetail }) {
  const queryClient = useQueryClient();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['erp', 'desks'] });
  const [dialog, setDialog] = useState<{ stage: DeskStage | null } | null>(null);
  const { terms } = useTenantProfile();

  const stages = [...desk.stages].sort((a, b) => a.position - b.position);
  const middle = stages.filter((s) => !s.isFirst && !s.isLast);
  const toReview = stages.filter((s) => !s.situationReviewed).length;

  // spec 017: situação escolhida direto na linha — salvar é revisar, então a
  // etapa sai de "a revisar" mesmo que a escolha seja a que já estava. É a tela
  // em que a operação valida as ~110 etapas (SC-001): um clique por etapa.
  const setSituation = useMutation({
    mutationFn: async ({ stage, situation }: { stage: DeskStage; situation: StageSituation }) => {
      const body: StageBody = { name: stage.name, maxTimeMinutes: stage.maxTimeMinutes, situation };
      const res = await api<DeskStage>(`/desks/${desk.id}/stages/${stage.id}`, { method: 'PUT', body });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const reorder = useMutation({
    mutationFn: async (ids: number[]) => {
      const res = await api<unknown>(`/desks/${desk.id}/stages/reorder`, {
        method: 'PUT',
        body: { ids },
      });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (stageId: number) => {
      const res = await api<void>(`/desks/${desk.id}/stages/${stageId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const move = (stageId: number, dir: -1 | 1) => {
    const ids = middle.map((s) => s.id);
    const i = ids.indexOf(stageId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    const a = ids[i];
    const b = ids[j];
    if (a === undefined || b === undefined) return;
    ids[i] = b;
    ids[j] = a;
    reorder.mutate(ids);
  };

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">Estágios</h2>
          <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
            {stages.length}
          </span>
          {toReview > 0 ? (
            <Badge variant="warning" title="Situação no padrão por falta de sugestão clara pelo nome">
              {toReview} a revisar
            </Badge>
          ) : null}
        </div>
        <Button size="sm" onClick={() => setDialog({ stage: null })}>
          <Plus className="size-4" strokeWidth={2.5} />
          Estágio
        </Button>
      </div>
      <ul className="space-y-1.5">
        {stages.map((s) => {
          const isMiddle = !s.isFirst && !s.isLast;
          const mi = middle.findIndex((m) => m.id === s.id);
          return (
            <li
              key={s.id}
              className="flex items-center justify-between gap-2 rounded-md border border-divider px-3 py-2 text-sm transition-colors hover:border-border"
            >
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <span className="truncate font-medium">{s.name}</span>
                {s.isFirst ? <Badge variant="primary">inicial</Badge> : null}
                {s.isLast ? <Badge variant="success">final</Badge> : null}
                {s.maxTimeMinutes != null ? (
                  <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                    <Timer className="size-3" />
                    {s.maxTimeMinutes} min
                  </span>
                ) : null}
                <Select
                  aria-label={`Situação de ${s.name}`}
                  title="Situação: o idioma comum das telas simples, do portal e dos relatórios"
                  className="ml-auto h-7 w-auto min-w-40 py-0 text-xs"
                  value={s.situation}
                  disabled={setSituation.isPending}
                  onChange={(e) =>
                    setSituation.mutate({ stage: s, situation: e.target.value as StageSituation })
                  }
                >
                  {STAGE_SITUATIONS.map((situation) => (
                    <option key={situation} value={situation}>
                      {situationLabel(terms, situation)}
                    </option>
                  ))}
                </Select>
                {!s.situationReviewed ? (
                  <Badge variant="warning" title="O nome não diz de quem é a vez — confira a situação">
                    a revisar
                  </Badge>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                {isMiddle ? (
                  <>
                    <DeskRowAction
                      label="Mover para cima"
                      disabled={mi <= 0 || reorder.isPending}
                      onClick={() => move(s.id, -1)}
                    >
                      <ArrowUp className="size-3.5" />
                    </DeskRowAction>
                    <DeskRowAction
                      label="Mover para baixo"
                      disabled={mi < 0 || mi === middle.length - 1 || reorder.isPending}
                      onClick={() => move(s.id, 1)}
                    >
                      <ArrowDown className="size-3.5" />
                    </DeskRowAction>
                  </>
                ) : null}
                <DeskRowAction label="Editar estágio" onClick={() => setDialog({ stage: s })}>
                  <Pencil className="size-3.5" />
                </DeskRowAction>
                {isMiddle ? (
                  <DeskRowAction
                    label="Remover estágio"
                    destructive
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Remover o estágio "${s.name}"?`)) remove.mutate(s.id);
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </DeskRowAction>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {(reorder.error ?? remove.error ?? setSituation.error) ? (
        <p className="mt-2 text-sm text-destructive">
          {errorMessage(reorder.error ?? remove.error ?? setSituation.error)}
        </p>
      ) : null}
      {dialog ? (
        <StageDialog deskId={desk.id} stage={dialog.stage} onClose={() => setDialog(null)} />
      ) : null}
    </section>
  );
}

function StageDialog({
  deskId,
  stage,
  onClose,
}: {
  deskId: number;
  stage: DeskStage | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { preset, terms } = useTenantProfile();
  const [name, setName] = useState(stage?.name ?? '');
  const [maxTime, setMaxTime] = useState(
    stage?.maxTimeMinutes != null ? String(stage.maxTimeMinutes) : ''
  );
  // spec 017: etapa nova nasce com a sugestão pelo nome (acompanha o que se
  // digita até a pessoa escolher); na edição, a situação gravada
  const [chosen, setChosen] = useState<StageSituation | null>(stage?.situation ?? null);
  const suggestion = initialStageSituation(
    { name, isFirst: stage?.isFirst ?? false, isLast: stage?.isLast ?? false },
    preset
  );
  const situation = chosen ?? suggestion.situation;

  const save = useMutation({
    mutationFn: async (body: StageBody) => {
      const res = stage
        ? await api<DeskStage>(`/desks/${deskId}/stages/${stage.id}`, { method: 'PUT', body })
        : await api<DeskStage>(`/desks/${deskId}/stages`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'desks'] });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const parsed = Number(maxTime);
    save.mutate({
      name: name.trim(),
      maxTimeMinutes: maxTime.trim() && Number.isInteger(parsed) && parsed > 0 ? parsed : null,
      situation,
    });
  };

  return (
    <Dialog open onClose={onClose} title={stage ? 'Editar estágio' : 'Novo estágio'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="stage-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="stage-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="stage-situation">Situação</Label>
          <Select
            id="stage-situation"
            value={situation}
            onChange={(e) => setChosen(e.target.value as StageSituation)}
          >
            {STAGE_SITUATIONS.map((option) => (
              <option key={option} value={option}>
                {situationLabel(terms, option)}
              </option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground">
            {chosen === null && !suggestion.reviewed
              ? 'O nome não diz de quem é a vez — confira antes de salvar.'
              : 'De quem é a vez nesta etapa. As telas simples, o portal e os relatórios falam por ela.'}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="stage-max-time">Tempo máximo (minutos)</Label>
          <Input
            id="stage-max-time"
            type="number"
            min={1}
            value={maxTime}
            onChange={(e) => setMaxTime(e.target.value)}
            placeholder="Sem limite"
          />
          <p className="text-xs text-muted-foreground">
            Passou disso na etapa, o atendimento aparece como parado.
          </p>
        </div>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
